/**
 * "New analysis" CTA — launches an A5 v2 keyword-driven SERP content analysis.
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Search } from 'lucide-react';
import { toast } from 'sonner';
import type { CompetitorAnalysis, Project } from '@seo/types';
import { Button, type ButtonProps } from '@/components/ui/button';
import { FormDialog, FormDialogBlock } from '@/components/ui/form-dialog';
import { TextField, FormField } from '@/components/ui/form-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { KeywordPicker, type KeywordPickerValue } from '@/components/keyword-picker';
import { extractFieldErrors, summarizeApiError } from '@/lib/api';
import { useCreateCompetitorAnalysis } from '@/features/competitor-analyses/use-create-competitor-analysis';

const FormSchema = z.object({
  project_id: z.string().uuid({ message: 'Pick a project' }),
  keyword: z.string().trim().min(2, 'Required').max(200, 'Too long'),
  language: z.string().trim().min(2, 'Required').max(10),
  country: z.string().trim().min(2, 'Required').max(3),
  top_n: z.coerce.number().int().min(3).max(20),
  render_js: z.enum(['auto', 'always', 'never']),
});
type FormValues = z.infer<typeof FormSchema>;

interface Props {
  projects: Project[];
  defaultProjectId?: string;
  /**
   * Path A pre-fills — when opened from a keyword row on the keyword-run
   * detail page. `defaultKeywordId` + `defaultKeywordRunId` should both be
   * set together; when only `defaultKeyword` is present it's treated as
   * free text.
   */
  defaultKeyword?: string;
  defaultKeywordId?: string;
  defaultKeywordRunId?: string;
  /** Optional trigger label — defaults to "New content analysis". Per-row launchers pass a short label like "Analyze". */
  label?: string;
  onCreated?: (a: CompetitorAnalysis) => void;
  buttonProps?: Omit<ButtonProps, 'onClick'>;
}

export function CompetitorAnalysisCreateButton({
  projects,
  defaultProjectId,
  defaultKeyword,
  defaultKeywordId,
  defaultKeywordRunId,
  label,
  onCreated,
  buttonProps,
}: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const trigger = (
    <Button {...buttonProps}>
      <Search className="h-4 w-4" />
      {label ?? 'New content analysis'}
    </Button>
  );

  if (projects.length === 0) {
    return (
      <FormDialogBlock
        open={open}
        onOpenChange={setOpen}
        trigger={trigger}
        title="No projects yet"
        description="Create a project first — analyses belong to a project."
      />
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      trigger={trigger}
      projects={projects}
      defaultProjectId={defaultProjectId}
      defaultKeyword={defaultKeyword}
      defaultKeywordId={defaultKeywordId}
      defaultKeywordRunId={defaultKeywordRunId}
      onCreated={onCreated}
    />
  );
}

function Dialog({
  open,
  onOpenChange,
  trigger,
  projects,
  defaultProjectId,
  defaultKeyword,
  defaultKeywordId,
  defaultKeywordRunId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  trigger: JSX.Element;
  projects: Project[];
  defaultProjectId?: string;
  defaultKeyword?: string;
  defaultKeywordId?: string;
  defaultKeywordRunId?: string;
  onCreated?: (a: CompetitorAnalysis) => void;
}): JSX.Element {
  const create = useCreateCompetitorAnalysis();
  const initialProjectId = defaultProjectId ?? (projects.length === 1 ? projects[0]?.id : '') ?? '';

  // Picker state — kept in a local ref alongside the form's `keyword` string so
  // we can send keyword_id / keyword_run_id when the user picked from a run.
  const initialPickerValue: KeywordPickerValue | null = defaultKeyword
    ? defaultKeywordId && defaultKeywordRunId
      ? {
          kind: 'picked',
          keywordId: defaultKeywordId,
          keywordRunId: defaultKeywordRunId,
          term: defaultKeyword,
          volume: null,
          difficulty: null,
          intent: null,
          clusterLabel: null,
        }
      : { kind: 'free-text', term: defaultKeyword }
    : null;
  const [pickerValue, setPickerValue] = useState<KeywordPickerValue | null>(initialPickerValue);

  const {
    handleSubmit,
    setValue,
    watch,
    setError,
    reset,
    register,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      project_id: initialProjectId,
      keyword: defaultKeyword ?? '',
      language: 'en',
      country: 'US',
      top_n: 10,
      render_js: 'auto',
    },
    mode: 'onBlur',
  });

  const projectId = watch('project_id');
  const renderJs = watch('render_js');

  // Reset picker state when the caller opens the dialog with different defaults.
  useEffect(() => {
    setPickerValue(initialPickerValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultKeyword, defaultKeywordId, defaultKeywordRunId]);

  const submit = handleSubmit(async (values) => {
    try {
      const created = await create.mutateAsync({
        project_id: values.project_id,
        keyword: values.keyword,
        language: values.language,
        country: values.country,
        scope: { top_n: values.top_n, render_js: values.render_js },
        ...(pickerValue?.kind === 'picked'
          ? {
              keyword_id: pickerValue.keywordId,
              keyword_run_id: pickerValue.keywordRunId,
            }
          : {}),
      });
      toast.success('Analysis launched', { description: created.keyword });
      reset();
      setPickerValue(null);
      onOpenChange(false);
      onCreated?.(created);
    } catch (e) {
      const fieldErrors = extractFieldErrors(e);
      for (const f of ['keyword', 'project_id', 'language', 'country'] as const) {
        if (fieldErrors[f]) setError(f, { type: 'server', message: fieldErrors[f] });
      }
      const summary = summarizeApiError(e, {
        fallbackTitle: 'Could not launch analysis',
      });
      toast.error(summary.title, { description: summary.description });
    }
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      icon={<Search className="h-5 w-5 text-primary" />}
      title="Analyze a SERP"
      description="Enter a keyword. We'll fetch the top-N Google results, extract structure + EEAT signals, cluster themes, and produce a recommended outline."
      submitLabel="Launch analysis"
      submitting={isSubmitting || create.isPending}
      onSubmit={submit}
    >
      <FormField label="Project" error={errors.project_id?.message}>
        <Select
          value={projectId}
          onValueChange={(v) => setValue('project_id', v, { shouldValidate: true })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Pick a project" />
          </SelectTrigger>
          <SelectContent>
            {projects.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name} — {p.domain.replace(/^https?:\/\//, '').replace(/\/$/, '')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>

      <FormField
        label="Keyword"
        error={errors.keyword?.message}
        hint={
          pickerValue?.kind === 'picked'
            ? "Picked from keyword research — we'll analyze who ranks for it and carry the link back to the source run."
            : 'Search across your keyword research, or type your own.'
        }
      >
        <KeywordPicker
          projectId={projectId || undefined}
          value={pickerValue}
          onChange={(v) => {
            setPickerValue(v);
            setValue('keyword', v.term, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }}
          autoFocus={!defaultKeyword}
          disabled={!projectId}
          placeholder="e.g. best running shoes for flat feet"
        />
        {/*
          Hidden binding so react-hook-form still validates `keyword` against
          the Zod min(2)/max(200) rule and picks up server field errors.
        */}
        <input type="hidden" {...register('keyword')} />
      </FormField>

      <div className="grid gap-4 md:grid-cols-3">
        <TextField
          label="Language"
          placeholder="en"
          hint="ISO 639-1"
          error={errors.language?.message}
          {...register('language')}
        />
        <TextField
          label="Country"
          placeholder="US"
          hint="ISO 3166-1 alpha-2"
          error={errors.country?.message}
          {...register('country')}
        />
        <TextField
          label="Top N"
          type="number"
          hint="How many SERP pages to analyze (3-20)"
          error={errors.top_n?.message}
          {...register('top_n')}
        />
      </div>

      <FormField label="Render mode" hint="Use `always` if the top pages are JS-heavy SPAs.">
        <Select
          value={renderJs}
          onValueChange={(v) =>
            setValue('render_js', v as FormValues['render_js'], { shouldValidate: true })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Auto (recommended)</SelectItem>
            <SelectItem value="always">Always render JS</SelectItem>
            <SelectItem value="never">Never render JS</SelectItem>
          </SelectContent>
        </Select>
      </FormField>
    </FormDialog>
  );
}
